import { fromBackendChannel, toBackendChannel } from "../channel-map";
import { type ApiFetchContext, apiFetch } from "../client";
import { buildFavoritePageUrl, type FavoritePageUrlInput } from "../path-adapter";
import type {
  AddFavoritesBodyRaw,
  AddFavoritesInput,
  DeleteFavoritesBodyRaw,
  DeleteFavoritesInput,
  FavoriteItem,
  FavoritePageRaw,
  FavoritePageResult,
} from "../types/favorites";

/**
 * POST /favorites/add — requires JWT (auth token in header).
 * Throws ApiError on 401 / 500. On success returns "Save success!" string.
 */
export async function addFavorite(
  ctx: ApiFetchContext,
  authToken: string,
  input: AddFavoritesInput,
): Promise<string> {
  const body: AddFavoritesBodyRaw = {
    type: input.type ?? "spu",
    channel: toBackendChannel(input.channel),
    targetId: input.spuNo,
    ...(input.subject !== undefined ? { subject: input.subject } : {}),
    ...(input.mainImage !== undefined ? { mainImage: input.mainImage } : {}),
    ...(input.priceCny !== undefined ? { price: input.priceCny } : {}),
    ...(input.detailUrl !== undefined ? { detailUrl: input.detailUrl } : {}),
  };
  return apiFetch<string>(ctx, "/favorites/add", {
    method: "POST",
    body,
    authToken,
  });
}

/**
 * GET /favorites/page — paged favorites list (type=spu by default).
 */
export async function queryFavoritePage(
  ctx: ApiFetchContext,
  authToken: string,
  input: FavoritePageUrlInput = {},
): Promise<FavoritePageResult> {
  const page = await apiFetch<FavoritePageRaw>(ctx, buildFavoritePageUrl(input), { authToken });
  const records: FavoriteItem[] = (page.records ?? []).map((r) => ({
    spuNo: r.spuNo,
    channel: fromBackendChannel(r.channel ?? "TAOBAO"),
    title: r.subject ?? "",
    imageUrl: r.mainImg ?? "",
    detailUrl: r.detailUrl ?? "",
    priceRubKopecks: Math.round((r.price ?? 0) * 100),
  }));
  return {
    records,
    total: page.total ?? records.length,
    pageNo: page.current ?? 1,
    pageSize: page.size ?? 10,
    pages: page.pages ?? 1,
  };
}

/**
 * DELETE /favorites/deleteBatch — requires JWT.
 */
export async function deleteFavoritesBatch(
  ctx: ApiFetchContext,
  authToken: string,
  input: DeleteFavoritesInput,
): Promise<string> {
  const body: DeleteFavoritesBodyRaw = {
    type: input.type ?? "spu",
    targetIds: input.spuNos,
  };
  return apiFetch<string>(ctx, "/favorites/deleteBatch", {
    method: "DELETE",
    body,
    authToken,
  });
}
